import {
  SetProfilePictureParams,
  UploadResponse,
} from '@pypestream/design-system';
import { normalizeFileName } from '.';

export type DataFileType =
  | 'scriptFile'
  | 'configFile'
  | 'assetData'
  | 'logo'
  | 'text/csv';

type UploadFileProps = Omit<SetProfilePictureParams, 'file'> & {
  url: string;
  type: DataFileType;
  fieldName?: string;
  file: File | File[] | FileList | null;
};

export const uploadFile = ({
  url,
  file,
  type,
  fieldName,
  load,
  error,
  progress,
  abort,
  onChange,
}: UploadFileProps): {
  abort: SetProfilePictureParams['abort'];
} => {
  const request = new XMLHttpRequest();
  request.withCredentials = true;
  const method = type === 'assetData' || type === 'logo' ? 'PUT' : 'POST';

  request.open(method, url);

  request.upload.onprogress = (e) =>
    progress?.(e.lengthComputable, e.loaded, e.total);

  const formData = new FormData();

  const renameFile = (name: string) =>
    type === 'assetData'
      ? normalizeFileName(name, { skipCodeFileException: true })
      : normalizeFileName(name);

  const filesArray: File[] = (
    file instanceof FileList
      ? Array.from(file)
      : Array.isArray(file)
        ? file
        : [file]
  ).flatMap((f) =>
    f
      ? [
          new File([f], renameFile(f.name), {
            type: f.type,
            lastModified: f.lastModified,
          }),
        ]
      : []
  );

  filesArray.forEach((f) => {
    formData.append(fieldName ?? type, f);
  });

  // Same message style as the HTTP-error branch below: falls back to a generic
  // upload/delete message when the backend gave no explicit `message`.
  const genericErrorMessage = () =>
    file ? 'Error uploading file' : 'Error deleting file';

  request.onload = () => {
    if (request.status >= 200 && request.status < 300) {
      load?.(request.responseText);

      try {
        const result = JSON.parse(request.response) as UploadResponse & {
          logo?: string;
        };
        onChange?.({
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - `data` is only present for the assetData endpoint
          data: result.data,
          // generic picture endpoints return `url`; the pype `/logo` endpoint returns
          // the updated channel with the new logo in `logo` (top-level `url` is null).
          url: result.url ?? result.logo,
          files: filesArray,
        });
      } catch {
        onChange?.({ data: undefined, files: filesArray });
      }
    } else {
      let errorText = '';

      try {
        errorText = JSON.parse(request.response).message;
      } catch (_error) {
        errorText = request.response;
      }

      const errorMessage = errorText ? errorText : genericErrorMessage();
      error?.(errorMessage);
    }
  };

  // Network failure / aborted request: the XHR fires `onerror` / `onabort`
  // instead of `onload`, so without these handlers the `error` callback would
  // never run and any Promise wrapping this upload (uploadBrandMomentFile,
  // uploadLogoFile) would hang forever (PSTUDIO-3710).
  request.onerror = () => {
    error?.(genericErrorMessage());
  };

  request.onabort = () => {
    error?.(genericErrorMessage());
  };

  const body = file ? formData : undefined;

  request.send(body);

  return {
    abort: () => {
      request.abort();

      abort?.();
    },
  };
};
